/**
 * @fileoverview Ilan Detay Navigasyon Hook'u
 *
 * Bu modul, ilan detay sayfasi navigasyonunu yoneten custom hook'u icerir.
 * React Router ile entegre olarak URL tabanli navigasyon saglar, bu sayede
 * ChatGPT'nin geri/ileri butonlari duzgun calisir.
 *
 * @module useDetailNavigation
 * @requires react-router-dom
 */

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * @typedef {Object} UseDetailNavigationOptions
 * @property {Function} updateWidgetState - Widget state guncelleme fonksiyonu
 * @property {Function} requestFullscreen - Tam ekran modu isteme fonksiyonu
 * @property {Object} widgetState - Mevcut widget state
 */

/**
 * @typedef {Object} UseDetailNavigationReturn
 * @property {string|null} selectedListingId - Secili ilan ID'si (URL veya state'ten)
 * @property {string|null} selectedListingIdFromUrl - URL'den alinan ilan ID'si
 * @property {Function} handleDetailClick - Ilan detayina git fonksiyonu
 * @property {Function} handleCloseDetail - Detay modali kapat fonksiyonu
 */

/**
 * Ilan detay sayfasi navigasyonunu yonetir.
 *
 * Bu hook, ilan detay goruntulenme icin URL tabanli navigasyon saglar.
 * React Router kullanarak URL'de `/detail/{id}` yolu olusturur, bu sayede
 * ChatGPT'nin geri/ileri butonlari ile navigasyon mumkun olur.
 *
 * **URL Yapisi:**
 * - Detay goruntulenme: `/detail/12345`
 * - Liste goruntulenme: `/`
 *
 * **State Senkronizasyonu:**
 * - URL degistiginde widget state guncellenir
 * - Widget state degistiginde URL guncellenir
 * - Her iki kaynak arasindan URL oncelikldir
 *
 * **ChatGPT Entegrasyonu:**
 * - Detay tiklandiginda otomatik fullscreen'e gecer
 * - Geri butonuyla detaydan cikisda state temizlenir
 *
 * @function useDetailNavigation
 * @param {UseDetailNavigationOptions} options - Hook opsiyonlari
 * @returns {UseDetailNavigationReturn} Navigasyon fonksiyonlari ve state
 *
 * @example
 * const {
 *   selectedListingId,
 *   handleDetailClick,
 *   handleCloseDetail,
 * } = useDetailNavigation({
 *   updateWidgetState,
 *   requestFullscreen,
 *   widgetState,
 * });
 *
 * // Ilan kartina tiklandiginda
 * <ListingCard onClick={() => handleDetailClick(listing.id)} />
 *
 * // Modal kapatildiginda
 * <Modal onClose={handleCloseDetail} />
 */
export function useDetailNavigation({ updateWidgetState, requestFullscreen, widgetState }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Get listing ID from URL path (e.g., /detail/12345)
  const selectedListingIdFromUrl = useMemo(() => {
    const match = location?.pathname?.match(/(?:^|\/)detail\/([^/]+)/);
    return match && match[1] ? match[1] : null;
  }, [location?.pathname]);

  // Compute selected listing ID - URL takes precedence
  const selectedListingId = selectedListingIdFromUrl || widgetState?.privateContent?.selectedListingId;

  const handleDetailClick = useCallback(
    async (id) => {
      // Set state BEFORE fullscreen to prevent "map flash"
      // (otherwise harita görünür, sonra detay view'e geçer)
      updateWidgetState((state) => ({
        ...state,
        privateContent: {
          ...state.privateContent,
          selectedListingId: id,
        },
      }));
      // Use React Router for ChatGPT's back/forward buttons
      navigate(`/detail/${id}`);
      await requestFullscreen();
    },
    [requestFullscreen, updateWidgetState, navigate]
  );

  const handleCloseDetail = useCallback(() => {
    updateWidgetState((state) => ({
      ...state,
      privateContent: {
        ...state.privateContent,
        selectedListingId: null,
      },
    }));
    navigate("/");
  }, [updateWidgetState, navigate]);

  // Sync widget state with URL when navigating back/forward via ChatGPT's ← → buttons
  useEffect(() => {
    if (selectedListingIdFromUrl) {
      updateWidgetState((state) => ({
        ...state,
        privateContent: {
          ...state.privateContent,
          selectedListingId: selectedListingIdFromUrl,
        },
      }));
    } else if (!selectedListingIdFromUrl && widgetState?.privateContent?.selectedListingId) {
      // URL has no listing ID but state has one - user navigated back
      updateWidgetState((state) => ({
        ...state,
        privateContent: {
          ...state.privateContent,
          selectedListingId: null,
        },
      }));
    }
  }, [selectedListingIdFromUrl, updateWidgetState, widgetState?.privateContent?.selectedListingId]);

  return {
    selectedListingId,
    selectedListingIdFromUrl,
    handleDetailClick,
    handleCloseDetail,
  };
}

export default useDetailNavigation;
